import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface TabState {
  data: any;
  isDirty: boolean;
}

export interface CaseWorkflowCommentLog {
  id: number;
  user: string;
  comment: string;
  created_at: Date;
}

// NEW: Interface for workflow history data - Updated to match API response
export interface WorkflowHistoryItem {
  stage: string;
  user: string;
  comment: string;
  created_at: string;
}

interface CaseDetailsState {
  overview: TabState;
  patient: TabState;
  clinical: TabState;
  event: TabState;
  triage: TabState;
  medical: TabState;
  historicalDrug: TabState;
  loading: boolean;
  error: string | null;
  caseId?: string;
  activeTab: string;
  actions: CaseWorkflowCommentLog[]; // OLD: Keep for backward compatibility
  workflowHistory: { // NEW: Add workflow history state
    data: WorkflowHistoryItem[];
    loading: boolean;
    error: string | null;
  };
  refetchWorkflow: { // NEW: Add refetch workflow state
    data: any;
    loading: boolean;
    error: string | null;
  };
  currentStudyTabData: {
    data: any; // replace `any` with the actual type of `data`
    loading: boolean;
    error: string | null;
  };
}

const initialTabState: TabState = {
  data: {},
  isDirty: false
};

const initialState: CaseDetailsState = {
  overview: { ...initialTabState },
  patient: { ...initialTabState },
  clinical: { ...initialTabState },
  event: { ...initialTabState },
  triage: { ...initialTabState },
  medical: { ...initialTabState, data: [] },
  currentStudyTabData: {
    data: null,
    loading: false,
    error: null
  },
  historicalDrug: { ...initialTabState, data: [] },
  actions: [], // OLD: Keep for backward compatibility
  workflowHistory: { // NEW: Initialize workflow history state
    data: [],
    loading: false,
    error: null,
  },
  refetchWorkflow: { // NEW: Initialize refetch workflow state
    data: [],
    loading: false,
    error: null,
  },
  loading: false,
  error: null,
  caseId: undefined,
  activeTab: 'general'
};

export const caseDetailsSlice = createSlice({
  name: 'caseDetails',
  initialState,
  reducers: {
    setCaseFieldValue: (
      state,
      action: PayloadAction<{
        tab: string;
        key: string;
        section: string;
        value: any;
        index?: number;
      }>
    ) => {
      const { tab, key, value, section, index } = action.payload;

      const tabState = state[tab as keyof CaseDetailsState] as TabState | undefined;
      if (!tabState || !tabState.data) return;
      if (!tabState.data[section]) {
        tabState.data[section] = index !== undefined ? [] : {};
      }
      if (index !== undefined) {
        if (!Array.isArray(tabState.data[section])) {
          tabState.data[section] = [];
        }

        if (!tabState.data[section][index]) {
          tabState.data[section][index] = {};
        }

        tabState.data[section][index][key] = value;
      } else {
        if (typeof tabState.data[section] !== "object" || Array.isArray(tabState.data[section])) {
          tabState.data[section] = {};
        }

        tabState.data[section][key] = value;
      }

      tabState.isDirty = true;
    },
    setCaseId: (state, action) => {
      state.caseId = action.payload;
    },

    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },
    markTabAsClean: (state, action: PayloadAction<string>) => {
      const tab = action.payload;
      const tabState = state[tab as keyof CaseDetailsState] as TabState | undefined;
      if (tabState) {
        tabState.isDirty = false;
      }
    },

    markTabAsDirty: (state, action: PayloadAction<string>) => {
      const tab = action.payload;
      const tabState = state[tab as keyof CaseDetailsState] as TabState | undefined;
      if (tabState) {
        tabState.isDirty = true;
      }
    },
    clearCaseInformationDetials: (state) => {
      state.overview = { ...initialTabState };
    },
    clearCaseDetails: (state) => {
      state.overview = { ...initialTabState };
      state.patient = { ...initialTabState };
      state.clinical = { ...initialTabState };
      state.event = { ...initialTabState };
      state.triage = { ...initialTabState };
      state.medical = { ...initialTabState, data: [] };
      state.historicalDrug = { ...initialTabState, data: [] };
      state.actions = [];
      state.workflowHistory = { // NEW: Reset workflow history
        data: [],
        loading: false,
        error: null,
      };
      state.activeTab = 'general';
      state.loading = false;
      state.error = null;
      state.caseId = undefined;
    }
  },
});

export const {
  setCaseFieldValue,
  setActiveTab,
  markTabAsClean,
  markTabAsDirty,
  clearCaseDetails,
  setCaseId,
  clearCaseInformationDetials
} = caseDetailsSlice.actions;

export default caseDetailsSlice.reducer;
